<template>
  <div class="relative">
    <div class="relative flex items-center">
      <!-- Input de recherche -->
      <input v-if="!selectedModel" v-model="searchQuery" type="text"
        class="w-full p-2 border border-gray-300 rounded-md mb-0"
        :class="{ 'bg-gray-100 cursor-not-allowed': readOnlyConfig?.isReadOnly }"
        :placeholder="getPlaceholder" :disabled="readOnlyConfig?.isReadOnly"
        @input="handleSearch" @focus="handleFocus" @blur="handleBlur" />

      <!-- Affichage du modèle sélectionné -->
      <div v-if="selectedModel !== null && selectedModel !== undefined"
        class="w-full p-2 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-between">
        <div class="flex-1">
          <div class="font-medium text-gray-900">
            {{ selectedModel.title || selectedModel.triid || 'Modèle sans titre' }}
            <span v-if="selectedModel.triid && selectedModel.title" class="text-gray-500 ml-1">{{ selectedModel.triid }}</span>
          </div>
          <div class="text-xs text-gray-600 truncate max-w-full">
            <span v-for="entry in howWhichSummary(selectedModel)" :key="entry.name" class="inline-block mr-2">
              <span class="font-semibold">{{ entry.name.split('.').pop() }}:</span> {{ entry.value }}
            </span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button v-if="!readOnlyConfig?.isReadOnly" title="Réinitialiser" class="p-2 text-red-500 hover:text-red-700"
            @click="resetAll">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Dropdown avec sections séparées -->
    <div v-if="showModelsDropdown && !selectedModel"
      class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-y-auto">
      <!-- Section : Mes modèles -->
      <div v-if="myModels.length > 0" class="section">
        <div class="section-header p-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
          <span class="text-sm font-medium text-blue-900">Mes modèles ({{ myModels.length }})</span>
        </div>
        <div v-for="model in myModels" :key="model.uuid"
          class="p-3 hover:bg-gray-100 cursor-pointer flex flex-col border-l-3 border-blue-500"
          @click="selectModel(model)">
          <div class="font-medium text-gray-900">
            {{ model.title || 'Modèle sans titre' }}
            <span v-if="getCompiledSchemaForModel(model)" class="text-gray-500 ml-1">
              ({{ getCompiledSchemaForModel(model)?.appellation }} / {{ getCompiledSchemaForModel(model)?.type }})
            </span>
          </div>
        </div>
      </div>
      <!-- Section : Modèles publics -->
      <div v-if="publicModels.length > 0" class="section">
        <div class="section-header p-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
          <span class="text-sm font-medium text-emerald-900">Modèles publics ({{ publicModels.length }})</span>
        </div>
        <div v-for="model in publicModels" :key="model.uuid"
          class="p-3 hover:bg-gray-100 cursor-pointer flex flex-col border-l-3 border-emerald-500"
          @click="selectModel(model)">
          <div class="font-medium text-gray-900">
            {{ model.title || 'Modèle sans titre' }}
            <span v-if="getCompiledSchemaForModel(model)" class="text-gray-500 ml-1">
              ({{ getCompiledSchemaForModel(model)?.appellation }} / {{ getCompiledSchemaForModel(model)?.type }})
            </span>
          </div>
        </div>
      </div>
      <!-- Message si aucun modèle -->
      <div v-if="myModels.length === 0 && publicModels.length === 0" class="p-4 text-center text-gray-500">
        <span>Aucun modèle HOW/WHICH trouvé</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useSchemasStore } from '@/stores/schemasStore';
import { compileSchemaWithIds } from '@/utils/schemaExportUtils';
import { computed, defineComponent, ref, watch } from 'vue';
import type { Memo } from '../../api/types/memos/types';
import { useMemos } from '../../composables/useAssets';

export default defineComponent({
  name: 'InstrumentationSelector',
  props: {
    userId: { type: String, required: true },
    assetsCacheStore: { type: Object, default: null },
    readOnlyConfig: { type: Object, default: () => ({ isReadOnly: false }) }
  },
  emits: ['model-selected', 'reset'],
  setup(props, { emit }) {
    const { filteredAssets: allMemos, isLoading } = useMemos({ defaultFilter: 'accessible' });
    const schemasStore = useSchemasStore();
    const searchQuery = ref('');
    const showModelsDropdown = ref(false);
    const selectedModel = ref<Memo | null>(null);

    const allModels = computed<Memo[]>(() =>
      allMemos.value.filter((memo: Memo) => memo.is_model && memo.data_entries && memo.data_entries.length > 0)
    );
    const myModels = computed<Memo[]>(() =>
      allModels.value.filter((memo: Memo) => memo.user_id === props.userId && filterMemo(memo, searchQuery.value))
    );
    const publicModels = computed<Memo[]>(() =>
      allModels.value.filter((memo: Memo) => memo.is_public && memo.user_id !== props.userId && filterMemo(memo, searchQuery.value))
    );

    function filterMemo(memo: Memo, query: string): boolean {
      if (!query) return true;
      const q = query.toLowerCase();
      const baseFields = [memo.title, memo.triid, memo.type, memo.schema_model?.appellation, memo.schema_model?.type];
      if (baseFields.some(f => (f || '').toLowerCase().includes(q))) return true;
      if (memo.data_entries && memo.data_entries.some(e => (e.name || '').toLowerCase().includes(q) || (e.value || '').toLowerCase().includes(q))) return true;
      return false;
    }

    function howWhichSummary(memo: Memo): { name: string; value: string }[] {
      if (!memo.data_entries) return [];
      return memo.data_entries.slice(0, 3).map(e => ({ name: e.name || '', value: e.value }));
    }

    // Fonction utilitaire pour compiler le schéma d'un modèle
    function getCompiledSchemaForModel(model: Memo): ReturnType<typeof compileSchemaWithIds> | null {
      if (!model.schema_model) return null;
      const fullSchema = schemasStore.schemas.find(s => s.uuid === model.schema_model?.uuid);
      if (!fullSchema) return null;
      return compileSchemaWithIds(
        fullSchema,
        schemasStore.sections,
        schemasStore.translations,
        schemasStore.schemaTranslations,
        schemasStore.fieldConfigs as any
      );
    }

    function handleFocus(): void {
      if (props.readOnlyConfig?.isReadOnly || selectedModel.value) return;
      showModelsDropdown.value = true;
    }
    function handleSearch(): void {
      if (props.readOnlyConfig?.isReadOnly || selectedModel.value) return;
      showModelsDropdown.value = true;
    }
    function handleBlur(): void {
      setTimeout(() => { showModelsDropdown.value = false; }, 200);
    }
    function selectModel(model: Memo): void {
      selectedModel.value = model;
      searchQuery.value = model.title || model.triid || '';
      showModelsDropdown.value = false;
      const compiledSchema = getCompiledSchemaForModel(model);
      emit('model-selected', { data_entries: model.data_entries, compiledSchema });
    }
    function resetAll(): void {
      selectedModel.value = null;
      searchQuery.value = '';
      showModelsDropdown.value = false;
      emit('reset');
      // Ajout : vider les données importées dans le parent
      emit('model-selected', { data_entries: [], compiledSchema: null });
    }
    const getPlaceholder = computed<string>(() => {
      if (props.readOnlyConfig?.isReadOnly) return 'Sélection impossible (lecture seule)';
      if (selectedModel.value) return selectedModel.value.title || selectedModel.value.triid || 'Modèle sélectionné';
      return 'Rechercher un modèle HOW/WHICH...';
    });

    watch(() => props.userId, () => { resetAll(); });

    return {
      searchQuery,
      showModelsDropdown,
      selectedModel,
      myModels,
      publicModels,
      isLoading,
      handleFocus,
      handleSearch,
      handleBlur,
      selectModel,
      resetAll,
      getPlaceholder,
      howWhichSummary,
      getCompiledSchemaForModel
    };
  }
});
</script>

<style scoped>
.border-l-3 {
  border-left-width: 3px;
}
.section-header {
  position: sticky;
  top: 0;
  z-index: 10;
}
</style> 